/**
 * New trial: a Veeva connection → a study record (listed by the server with
 * its own VQL) → the expected TMF artifacts (essential by default) → optional
 * Protocol / SOP uploads. The trial is created first; the uploads follow it.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  FileInput,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  createTrial,
  listStudyRecords,
  uploadTrialSource,
  type ArtifactOut,
  type EtmfConnectionOut,
  type StudyRecordOut,
  type TrialDetailOut,
} from "@/shared/api/etmfCompleteness";
import { toastApiError } from "@/shared/ui/toast";

interface Props {
  opened: boolean;
  onClose: () => void;
  connections: EtmfConnectionOut[];
  artifacts: ArtifactOut[];
  onCreated: (trial: TrialDetailOut) => void;
}

export function NewTrialModal({ opened, onClose, connections, artifacts, onCreated }: Props) {
  const getToken = useAccessToken();
  const [connectionId, setConnectionId] = useState<string | null>(
    connections.find((c) => c.usable)?.connection_id ?? null,
  );
  const [records, setRecords] = useState<StudyRecordOut[] | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>(() =>
    artifacts.filter((a) => a.essential).map((a) => a.number),
  );
  const [protocol, setProtocol] = useState<File | null>(null);
  const [sops, setSops] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelected(artifacts.filter((a) => a.essential).map((a) => a.number));
  }, [artifacts]);

  useEffect(() => {
    if (!opened || !connectionId) return;
    let cancelled = false;
    setRecords(null);
    setRecordError(null);
    setRecordId(null);
    void (async () => {
      try {
        const res = await listStudyRecords(await getToken(), connectionId);
        if (!cancelled) setRecords(res.items);
      } catch (err) {
        if (!cancelled) {
          setRecords([]);
          setRecordError(err instanceof Error ? err.message : "Could not list the studies.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, connectionId, getToken]);

  const record = records?.find((r) => r.id === recordId) ?? null;
  const zones = useMemo(() => {
    const out = new Map<string, ArtifactOut[]>();
    for (const a of artifacts) {
      const key = `${a.zone_number} ${a.zone_name}`;
      out.set(key, [...(out.get(key) ?? []), a]);
    }
    return [...out.entries()];
  }, [artifacts]);

  const submit = async () => {
    if (!connectionId || !record) return;
    setSaving(true);
    try {
      const token = await getToken();
      let trial = await createTrial(token, {
        name: name.trim() || record.name,
        connection_id: connectionId,
        study_record_id: record.id,
        study_label: record.name,
        artifact_numbers: selected,
      });
      if (protocol) trial = await uploadTrialSource(token, trial.trial_id, "protocol", protocol);
      for (const f of sops) trial = await uploadTrialSource(token, trial.trial_id, "sop", f);
      onCreated(trial);
    } catch (err) {
      toastApiError("Could not create the trial", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="New trial" size="lg">
      <Stack gap="sm">
        <Select
          label="Veeva connection"
          description={
            connections.some((c) => !c.usable)
              ? "A greyed-out connection is not shared with you — ask its owner to share it with use."
              : undefined
          }
          // A connection this person can see but not use stays in the list,
          // disabled and naming its owner: the picker then explains an absence
          // instead of hiding it (ADR-0015 §5 — seeing is the Administrator
          // read bypass, using needs a grant).
          data={connections.map((c) => ({
            value: c.connection_id,
            label: c.usable
              ? c.display_name
              : `${c.display_name} — not shared with you${
                  c.owner_display_name ? ` (owner: ${c.owner_display_name})` : ""
                }`,
            disabled: !c.usable,
          }))}
          value={connectionId}
          onChange={setConnectionId}
          allowDeselect={false}
          required
        />
        {records === null ? (
          <Loader size="xs" aria-label="Loading studies" />
        ) : recordError ? (
          <Alert color="red" title="Could not list the studies">
            {recordError}
          </Alert>
        ) : records.length === 0 ? (
          <Alert color="yellow" title="No study found">
            This vault has no Study records you can read. Check the connection in Integrations.
          </Alert>
        ) : (
          <Select
            label="Study record"
            description="Listed from the vault's Study object — you never write VQL."
            data={records.map((r) => ({ value: r.id, label: r.name }))}
            value={recordId}
            onChange={(v) => {
              setRecordId(v);
              const r = records.find((x) => x.id === v);
              if (r && !name.trim()) setName(r.name);
            }}
            searchable
            required
          />
        )}
        <TextInput
          label="Trial name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          maxLength={200}
        />
        <FileInput
          label="Trial Protocol"
          description="Scores the vs Protocol column and the study-metadata check."
          placeholder="Optional — you can upload it later"
          value={protocol}
          onChange={setProtocol}
          clearable
        />
        <FileInput
          label="SOPs"
          description="Reference documents for the vs SOP column."
          placeholder="Optional"
          multiple
          value={sops}
          onChange={setSops}
          clearable
        />
        <div>
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Expected artifacts ({selected.length})
            </Text>
            <Group gap="xs">
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={() =>
                  setSelected(artifacts.filter((a) => a.essential).map((a) => a.number))
                }
              >
                Essential
              </Button>
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={() => setSelected(artifacts.map((a) => a.number))}
              >
                All
              </Button>
            </Group>
          </Group>
          <ScrollArea.Autosize mah={260} type="auto">
            <Checkbox.Group value={selected} onChange={setSelected}>
              <Stack gap={4} mt={4}>
                {zones.map(([zone, list]) => (
                  <Stack key={zone} gap={2}>
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                      {zone}
                    </Text>
                    {list.map((a) => (
                      <Checkbox
                        key={a.number}
                        value={a.number}
                        size="xs"
                        label={`${a.number} ${a.name}`}
                      />
                    ))}
                  </Stack>
                ))}
              </Stack>
            </Checkbox.Group>
          </ScrollArea.Autosize>
        </div>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={!record || selected.length === 0}
          >
            Create trial
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
